/**
 * Row titles for the Upload page's per-month file table.
 *
 * `client_processing_queue` docs only carry `displayName` when they arrived via
 * the admin ZIP / EML ingestion paths (`formatUploadDisplayName`). Every
 * self-serve and script-seeded tenant therefore fell back to the raw filename,
 * which is also what the row's expander already shows under "Original
 * filename:": the same string twice, and no indication of which commission
 * PERIOD the file covers.
 *
 * Every queue doc does carry `periodMonth` ('YYYY-MM'), which is what an
 * operator is actually looking for, so it becomes the title. Where several
 * files in the same group cover the same period, the existing `runNumber`
 * disambiguates them.
 */

/** One `client_processing_queue` row, as far as the title is concerned. */
export interface UploadRowFile {
  id: string;
  displayName?: string | null;
  periodMonth?: string | null;
  runNumber?: number | null;
  fileName?: string | null;
}

/** 'YYYY-MM' -> 'July 2026'. Any other shape (including 'November') -> null. */
export function formatPeriodLabel(periodMonth?: string | null): string | null {
  const match = /^(\d{4})-(\d{2})$/.exec(periodMonth || '');
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(Number(match[1]), month - 1, 1).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Title for every file in one rendered group, keyed by file id.
 *
 * `displayName` -> period label (plus ' · Run N' when the group holds several
 * files for that period) -> filename. Computed for the whole group at once so
 * the "several files share this period" test is done exactly once.
 */
export function resolveUploadRowTitles(files: UploadRowFile[]): Map<string, string> {
  const perPeriodCount = new Map<string, number>();
  for (const file of files) {
    const label = formatPeriodLabel(file.periodMonth);
    if (label) perPeriodCount.set(label, (perPeriodCount.get(label) || 0) + 1);
  }

  const titles = new Map<string, string>();
  for (const file of files) {
    if (file.displayName) {
      titles.set(file.id, file.displayName);
      continue;
    }
    const label = formatPeriodLabel(file.periodMonth);
    if (label) {
      const shared = (perPeriodCount.get(label) || 0) > 1;
      const run = typeof file.runNumber === 'number' ? file.runNumber : null;
      titles.set(file.id, shared && run !== null ? `${label} · Run ${run}` : label);
      continue;
    }
    titles.set(file.id, file.fileName || '');
  }
  return titles;
}
